// Subscription + entitlement gating.
//
// Single source of truth for "can this user do X?". UI screens import the
// helpers below instead of inspecting `users/{uid}` fields directly. The
// server (functions-python/app/utils/feature_gate.py) mirrors this logic
// for any operation that touches money, leaderboards, or AI calls.
//
// Field shape on `users/{uid}` (written exclusively by the PayPal callable
// `paypal_record_subscription` and the `paypal_webhook` handler — and by
// the harness PayPal bridge against the Firestore emulator. Never written
// by the client; see firestore.rules `touchesServerOnlyFields`):
//
//   subscription: {
//     tier:    "free" | "pro_monthly" | "pro_annual",
//     status:  "none" | "active" | "trialing" | "past_due" | "canceled",
//     planId,                    // PayPal-facing SKU, e.g. "angler-pro-monthly"
//     paypalSubscriptionId,
//     currentPeriodEnd: Timestamp,
//     cancelAtPeriodEnd: boolean,
//     updatedAt: Timestamp,
//   }
//   tournamentPassTier: "none" | "annual" | "event",
//   tournamentPassExpiresAt: Timestamp,    // annual only
//   tournamentPassEventIds: string[],      // per-event entitlements
//
// Tier rules come from docs/revenue-models/subscriptions.md §3a/§3e.

// ---------------------------------------------------------------------------
// Quotas — keep in sync with subscriptions.md and the dashboard pricing UI.
// ---------------------------------------------------------------------------

export const PRO_TIERS = ["pro_monthly", "pro_annual"];

export const FREE_TRACKED_SPECIES = 1;
export const FREE_TRACKED_LURES = 1;
export const PRO_TRACKED_SPECIES = 10;
export const PRO_TRACKED_LURES = 25; // soft cap; bumpable later

export const FREE_VIDEO_CLIPS_PER_MONTH = 1;
export const PRO_VIDEO_SESSIONS_PER_MONTH = 20;

// AI Pattern Insight: Pro required AND >= this many catch-sessions at the
// user's home station. The data-maturity gate is in usePatternTier.js; this
// is only the subscription side of the check.
export const AI_MIN_CATCH_SESSIONS = 10;

// "Active" PayPal-mapped statuses that grant entitlements. `past_due` is a
// grace window where PayPal is still retrying — keep access on so we don't
// punish a transient card decline.
const ACTIVE_STATUSES = new Set(["active", "trialing", "past_due"]);

// ---------------------------------------------------------------------------
// Normalization
// ---------------------------------------------------------------------------

function toMillis(value) {
  if (!value) return 0;
  if (typeof value === "number") return value;
  if (typeof value.toMillis === "function") return value.toMillis();
  if (typeof value.seconds === "number") {
    return value.seconds * 1000 + Math.floor((value.nanoseconds || 0) / 1e6);
  }
  if (value instanceof Date) return value.getTime();
  const parsed = Date.parse(String(value));
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Returns a normalized subscription view for any caller that just wants
 * booleans without juggling Firestore field shapes.
 */
export function getSubscriptionView(profile) {
  const sub = (profile && typeof profile.subscription === "object") ? profile.subscription : {};
  const tier = String(sub.tier || "free").toLowerCase();
  const status = String(sub.status || "none").toLowerCase();
  const periodEnd = toMillis(sub.currentPeriodEnd);
  const isPro = PRO_TIERS.includes(tier) && ACTIVE_STATUSES.has(status);
  const isTrialing = status === "trialing";

  const passTier = String(profile?.tournamentPassTier || "none").toLowerCase();
  const passExpires = toMillis(profile?.tournamentPassExpiresAt);
  const passEvents = Array.isArray(profile?.tournamentPassEventIds)
    ? profile.tournamentPassEventIds.map(String)
    : [];

  return {
    tier,
    status,
    isProActive: isPro,
    isTrialing,
    currentPeriodEndMs: periodEnd,
    daysLeft: periodEnd ? Math.max(0, Math.ceil((periodEnd - Date.now()) / 86400000)) : null,
    cancelAtPeriodEnd: !!sub.cancelAtPeriodEnd,
    tournamentPassTier: passTier,
    tournamentPassExpiresMs: passExpires,
    tournamentPassEventIds: passEvents,
    raw: sub,
  };
}

// ---------------------------------------------------------------------------
// Entitlement checks
// ---------------------------------------------------------------------------

export function isProActive(profile) {
  return getSubscriptionView(profile).isProActive;
}

/**
 * Tournament participation — Pro covers it free per §3e ("never charge Pro
 * subscribers twice"). Otherwise the user needs an active annual pass or a
 * per-event entitlement matching this tournament.
 */
export function hasActiveTournamentPass(profile, tournamentId = "") {
  const view = getSubscriptionView(profile);
  if (view.isProActive) return true;
  if (view.tournamentPassTier === "annual") {
    return !view.tournamentPassExpiresMs || view.tournamentPassExpiresMs > Date.now();
  }
  if (view.tournamentPassTier === "event" && tournamentId) {
    return view.tournamentPassEventIds.includes(String(tournamentId));
  }
  return false;
}

export function canEnterTournament(profile, tournamentId = "") {
  return hasActiveTournamentPass(profile, tournamentId);
}

/**
 * AI Pattern Insight is gated on BOTH (a) Pro subscription and (b) enough
 * catch data to actually be useful. Callers pass the data-side number from
 * usePatternTier; we own the subscription side.
 */
export function canUseAIPatternInsight(profile, catchSessions = 0) {
  return isProActive(profile) && catchSessions >= AI_MIN_CATCH_SESSIONS;
}

export function canUsePreparationScore(profile) {
  return isProActive(profile);
}

export function canUseVoiceCatchLogging(profile) {
  return isProActive(profile);
}

/**
 * Certified catches (tournament-eligible video catches) require an active
 * tournament entitlement — Pro or a Pass for the specific tournament.
 */
export function canCertifyCatch(profile, tournamentId = "") {
  return hasActiveTournamentPass(profile, tournamentId);
}

// ---------------------------------------------------------------------------
// Quotas
// ---------------------------------------------------------------------------

export function maxTrackedSpecies(profile) {
  return isProActive(profile) ? PRO_TRACKED_SPECIES : FREE_TRACKED_SPECIES;
}

export function maxTrackedLures(profile) {
  return isProActive(profile) ? PRO_TRACKED_LURES : FREE_TRACKED_LURES;
}

export function videoQuotaPerMonth(profile) {
  return isProActive(profile)
    ? PRO_VIDEO_SESSIONS_PER_MONTH
    : FREE_VIDEO_CLIPS_PER_MONTH;
}

export function canUploadCatchVideo(profile, currentMonthCount = 0) {
  return currentMonthCount < videoQuotaPerMonth(profile);
}

/**
 * True when the user redeemed a book QR and is eligible for the reader
 * discount. Written server-side by the /v1/book/redeem endpoint; the
 * dashboard billing flow reads this to apply the discounted price. Never
 * written by the client (see firestore.rules `touchesServerOnlyFields`).
 */
export function hasBookDiscount(profile) {
  const discount = profile && typeof profile.bookDiscount === "object"
    ? profile.bookDiscount
    : null;
  if (!discount) return false;
  return String(discount.status || "").toLowerCase() === "eligible";
}

/**
 * The reader offer the user redeemed (e.g. "six_months_free"), or null. The
 * dashboard billing flow uses this to default book buyers into their plan.
 */
export function getBookOffer(profile) {
  if (!hasBookDiscount(profile)) return null;
  return String(profile.bookDiscount.offer || "") || null;
}
